import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { getAgent } from '../lib/api'

export function AgentMetadataPage() {
  const { agent_key } = useParams<{ agent_key: string }>()
  const navigate = useNavigate()
  const [metadata, setMetadata] = useState<Record<string, unknown> | null>(null)
  const [agentName, setAgentName] = useState<string | null>(null)
  const [metadataUri, setMetadataUri] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!agent_key) return

    const fetchAgent = async () => {
      try {
        setLoading(true)
        const agent = await getAgent(agent_key)
        setMetadata(agent.metadata || null)
        setAgentName(agent.name || `Agent #${agent.token_id}`)
        setMetadataUri(agent.metadata_uri || null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load metadata')
      } finally {
        setLoading(false)
      }
    }

    fetchAgent()
  }, [agent_key])

  const getRawMetadataUrl = (uri: string) => {
    if (uri.startsWith('ipfs://')) {
      return `https://ipfs.io/ipfs/${uri.replace('ipfs://', '')}`
    }
    if (uri.startsWith('ar://')) {
      return `https://arweave.net/${uri.replace('ar://', '')}`
    }
    return uri
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-terminal flex items-center justify-center">
        <div className="text-[var(--text-muted)] font-mono">Loading metadata...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-terminal flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 font-mono mb-4">{error}</div>
          <button
            onClick={() => navigate(-1)}
            className="text-[var(--accent-teal)] font-mono hover:underline"
          >
            Go back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-terminal">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Link
            to={`/agents/${encodeURIComponent(agent_key || '')}`}
            className="inline-flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] font-mono text-sm mb-4 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Agent
          </Link>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-display font-medium text-[var(--text-primary)]">
                Metadata
              </h1>
              <p className="text-sm font-mono text-[var(--text-muted)] mt-1">
                {agentName}
              </p>
            </div>

            {metadataUri && (
              <a
                href={getRawMetadataUrl(metadataUri)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--border-default)] text-[var(--text-secondary)] font-mono text-sm hover:bg-[var(--bg-tertiary)] transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                View Raw
              </a>
            )}
          </div>
        </motion.div>

        {/* Metadata Content */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-default)] overflow-hidden"
        >
          {metadata ? (
            <div className="p-6">
              <pre className="font-mono text-sm text-[var(--text-secondary)] overflow-auto whitespace-pre-wrap break-all">
                <JsonView data={metadata} />
              </pre>
            </div>
          ) : (
            <div className="p-6 text-center text-[var(--text-muted)] font-mono">
              No metadata available
            </div>
          )}
        </motion.div>

        {/* URI Info */}
        {metadataUri && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mt-4 px-4 py-3 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-subtle)]"
          >
            <div className="text-xs text-[var(--text-muted)] font-mono mb-1">Source URI</div>
            <div className="text-sm font-mono text-[var(--text-secondary)] break-all">
              {metadataUri}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  )
}

// Syntax-highlighted JSON viewer
function JsonView({ data, indent = 0 }: { data: unknown; indent?: number }) {
  const indentStr = '  '.repeat(indent)
  const nextIndent = indent + 1
  const nextIndentStr = '  '.repeat(nextIndent)

  if (data === null) {
    return <span className="text-[var(--text-muted)]">null</span>
  }

  if (typeof data === 'boolean') {
    return <span className="text-[var(--accent-teal)]">{data ? 'true' : 'false'}</span>
  }

  if (typeof data === 'number') {
    return <span className="text-[var(--accent-gold)]">{data}</span>
  }

  if (typeof data === 'string') {
    // Check if it's a URL
    if (data.startsWith('http://') || data.startsWith('https://') || data.startsWith('ipfs://') || data.startsWith('ar://')) {
      const displayUrl = data.startsWith('ipfs://')
        ? `https://ipfs.io/ipfs/${data.replace('ipfs://', '')}`
        : data.startsWith('ar://')
        ? `https://arweave.net/${data.replace('ar://', '')}`
        : data
      return (
        <a
          href={displayUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[var(--badge-a2a)] hover:underline"
        >
          "{data}"
        </a>
      )
    }
    return <span className="text-[var(--status-online)]">"{data}"</span>
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <span className="text-[var(--text-primary)]">[]</span>
    }
    return (
      <>
        <span className="text-[var(--text-primary)]">[</span>
        {'\n'}
        {data.map((item, i) => (
          <span key={i}>
            {nextIndentStr}
            <JsonView data={item} indent={nextIndent} />
            {i < data.length - 1 ? ',' : ''}
            {'\n'}
          </span>
        ))}
        {indentStr}
        <span className="text-[var(--text-primary)]">]</span>
      </>
    )
  }

  if (typeof data === 'object') {
    const entries = Object.entries(data as Record<string, unknown>)
    if (entries.length === 0) {
      return <span className="text-[var(--text-primary)]">{'{}'}</span>
    }
    return (
      <>
        <span className="text-[var(--text-primary)]">{'{'}</span>
        {'\n'}
        {entries.map(([key, value], i) => (
          <span key={key}>
            {nextIndentStr}
            <span className="text-[var(--badge-mcp)]">"{key}"</span>
            <span className="text-[var(--text-primary)]">: </span>
            <JsonView data={value} indent={nextIndent} />
            {i < entries.length - 1 ? ',' : ''}
            {'\n'}
          </span>
        ))}
        {indentStr}
        <span className="text-[var(--text-primary)]">{'}'}</span>
      </>
    )
  }

  return <span>{String(data)}</span>
}
